import React, { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from './Message.module.scss';
import { Button, Icon, IconList } from 'components/core';
import { IconType } from 'components/core/Icon/Icon';

export type MessageType = 'primary' | 'info' | 'success' | 'warning' | 'danger';
export const semanticTypes: MessageType[] = [
  'info',
  'warning',
  'danger',
  'success',
];

/**
 * Internal padding. `md` (default) is the standard message spacing; `sm`
 * tightens the vertical padding (keeping the `md` side padding) for dense
 * surfaces such as inline banners in a list; `lg` widens the padding for
 * prominent, standalone promo-style messages.
 */
export type MessageSize = 'sm' | 'md' | 'lg';

type BaseProps = {
  children: ReactNode;
  icon?: IconType | false;
  className?: string;
  type?: MessageType;
  variant?: 'subtle' | 'prominent';
  size?: MessageSize;
  dismissible?: boolean;
  isVisible?: boolean;
  onClose?: () => void;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'role'>;

/**
 * The wrapper is a live region by default (`role="status"`, polite) so a
 * message that appears after load is announced without interrupting. The two
 * options are mutually exclusive:
 * - `role` overrides that role (e.g. `alert` for a time-critical message).
 * - `presentational` drops the live region entirely, for content that is
 *   advisory and static once shown so it is read only during normal page
 *   navigation rather than announced on insert.
 */
type RoleProps =
  | { role?: React.AriaRole; presentational?: never }
  | { presentational: true; role?: never };

type Props = BaseProps & RoleProps;

const IconLookup = {
  primary: IconList.solid.faCircleCheck,
  info: IconList.solid.faCircleInfo,
  success: IconList.solid.faCircleCheck,
  warning: IconList.solid.faTriangleExclamation,
  danger: IconList.solid.faCircleXmark,
};

export const Message = ({
  children,
  className,
  icon,
  type = 'info',
  variant = 'subtle',
  size = 'md',
  role = 'status',
  presentational,
  dismissible = true,
  isVisible = true,
  onClose = () => '',
  ...props
}: Props) => {
  const [isOpen, setIsOpen] = useState(isVisible);

  useEffect(() => {
    setIsOpen(isVisible);
  }, [isVisible]);

  if (!isOpen) return null;

  if (icon !== false) {
    icon = icon ?? IconLookup[type];
  }

  const ariaLabel = (type: MessageType) => {
    return semanticTypes.includes(type) ? type : undefined;
  };

  return (
    <section
      className={classNames(
        styles.Message,
        className,
        styles[type],
        styles[variant],
        styles[size]
      )}
      role={presentational ? undefined : role}
      {...props}
    >
      {icon && (
        <div className={styles.IconContainer}>
          <Icon
            icon={icon}
            size="xl"
            className={styles.MessageIcon}
            aria-label={ariaLabel(type)}
          />
        </div>
      )}
      <div className={styles.MessageContainer}>{children}</div>
      {dismissible && (
        <div className={styles.IconContainer}>
          <Button
            iconOnly
            size="xs"
            variant="ghost"
            aria-label="Dismiss message"
            className={styles.closeButton}
            onClick={() => {
              setIsOpen(false);
              onClose();
            }}
          >
            <Icon icon={IconList.light.faClose} size="lg">
              Close
            </Icon>
          </Button>
        </div>
      )}
    </section>
  );
};

// Some Message uses are referred to as SectionMessage in Figma designs so this alias
// allows code to match designs.
export const SectionMessage = Message;
